import React from 'react';
import PropTypes from 'prop-types';
import InputCalculator from '../common/InputCalculator';

const TWO_DECIMALS = /^\d+(\.\d{1,2})?$/;
const BASE_INPUT_CHARS = /^[\d+\-*/.()%\u00d7\u00f7 ]*$/;
const RATE_INPUT_CHARS = /^\d{0,3}(\.\d{0,2})?$/;

export const COMMISSION_TASK_TYPE_NAME = 'commission';

export const isValidCommissionBase = (value) => TWO_DECIMALS.test(String(value ?? '').trim()) && Number(value) > 0;

export const isValidCommissionRate = (value) => TWO_DECIMALS.test(String(value ?? '').trim())
    && Number(value) >= 0.01 && Number(value) <= 100;

export const isAllowedCommissionBaseInput = (value) => BASE_INPUT_CHARS.test(value);

export const isAllowedCommissionRateInput = (value) => RATE_INPUT_CHARS.test(value);

// Works in whole cents / hundredths of a percent to avoid floating-point drift.
export const calculateCommissionAmount = (base, rate) => {
    if (!isValidCommissionBase(base) || !isValidCommissionRate(rate)) return '';
    const baseCents = Math.round(Number(base) * 100);
    const rateHundredths = Math.round(Number(rate) * 100);
    return (Math.round((baseCents * rateHundredths) / 10000) / 100).toFixed(2);
};

export const getCommissionErrors = (formData, isCommission = true) => {
    const errors = {};
    if (!isCommission) return errors;
    if (!isValidCommissionBase(formData.commissionBaseAmount)) {
        errors.commissionBaseAmount = 'Base amount must be greater than 0 (up to 2 decimals)';
    }
    if (!isValidCommissionRate(formData.commissionRate)) {
        errors.commissionRate = 'Rate must be between 0.01 and 100 (up to 2 decimals)';
    }
    return errors;
};

export const isCommissionTaskTypeSelected = (taskTypeList, taskTypeId) => {
    const commissionType = taskTypeList?.find((t) => t?.typeName?.toLowerCase?.() === COMMISSION_TASK_TYPE_NAME);
    return commissionType !== undefined && Number(taskTypeId) === Number(commissionType.id);
};

export const getChargeDefaultAmount = (isCommission, commissionAmount) => (isCommission ? commissionAmount : undefined);

// Post Charges debits the saved commission, so an invalid base/rate blocks posting.
// Returns null when posting may continue.
export const getCommissionPostIssue = (isCommission, formData) => {
    const errors = getCommissionErrors(formData, isCommission);
    return Object.keys(errors).length > 0
        ? { errors, message: 'Enter a valid commission base amount and rate.' }
        : null;
};

export const applyCommissionPayload = (payload, isCommission, commissionAmount) => {
    if (!isCommission) {
        return { ...payload, commissionBaseAmount: null, commissionRate: null, commissionAmount: null };
    }
    return {
        ...payload,
        commissionBaseAmount: Number(payload.commissionBaseAmount),
        commissionRate: Number(payload.commissionRate),
        commissionAmount: Number(commissionAmount),
    };
};

const CommissionSection = ({ formData, errors, isEditable, commissionAmount, onBaseChange, onRateChange }) => (
    <div className="row mb-3">
        <div className="col-12">
            <div className="commissionCard">
                <div className="commissionCard__title">Commission</div>
                <div className="row">
                    <div className="col-lg-5 col-md-6 col-sm-12 mobMb-10">
                        <div className="input-label-txt">Base Amount ($) <span className="require-field">*</span></div>
                        <InputCalculator
                            value={String(formData.commissionBaseAmount ?? '')}
                            onChange={onBaseChange}
                            placeholder="Enter base amount"
                            maxLength={30}
                            disabled={!isEditable}
                            percentMode
                            className={errors.commissionBaseAmount ? 'errorField' : ''}
                        />
                        <div className="commissionCard__help">Amount the commission is charged on.</div>
                        {errors.commissionBaseAmount && <div className="requiredthis">{errors.commissionBaseAmount}</div>}
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                        <div className="input-label-txt">Commission Rate (%) <span className="require-field">*</span></div>
                        <div className="input-group">
                            <input
                                className={`form-control ${errors.commissionRate ? 'errorField' : ''}`}
                                type="text"
                                inputMode="decimal"
                                name="commissionRate"
                                placeholder="0.00"
                                value={formData.commissionRate ?? ''}
                                onChange={onRateChange}
                                disabled={!isEditable}
                            />
                            <span className="commissionCard__suffix">%</span>
                        </div>
                        <div className="commissionCard__help">Up to 2 decimals.</div>
                        {errors.commissionRate && <div className="requiredthis">{errors.commissionRate}</div>}
                    </div>
                    <div className="col-lg-4 col-md-12 col-sm-12 mobMb-10">
                        <div className="input-label-txt">Commission Amount ($)</div>
                        <input
                            className="form-control commissionCard__amount"
                            type="text"
                            value={commissionAmount}
                            readOnly
                            disabled
                            aria-label="Commission Amount"
                        />
                        <div className="commissionCard__help">Calculated automatically. This is what Post Charges debits.</div>
                    </div>
                </div>
                {commissionAmount && (
                    <div className="commissionCard__summary">
                        $ {Number(formData.commissionBaseAmount).toFixed(2)} &times; {Number(formData.commissionRate)}% = <strong>$ {commissionAmount}</strong>
                    </div>
                )}
            </div>
        </div>
    </div>
);

CommissionSection.propTypes = {
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    isEditable: PropTypes.bool,
    commissionAmount: PropTypes.string,
    onBaseChange: PropTypes.func.isRequired,
    onRateChange: PropTypes.func.isRequired,
};

export default CommissionSection;
